import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Popover } from "@headlessui/react";

import { useHistory } from "react-router";
import { Category } from "@core/types";
import { useGetAllCategoriesQuery } from "../../providers/categories";
import { IonIcon } from "@ionic/react";
import { closeOutline, menuOutline } from "ionicons/icons";
import { Image } from "../Image/Image";
import classNames from "classnames/bind";
import styles from "./DropdownCatalog.module.scss";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

export const DropdownCatalog = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const { data: { allCategories, mainCategories } = {} } = useGetAllCategoriesQuery();

  useEffect(() => {
    if (mainCategories && mainCategories.length > 0) {
      setActiveCategory(mainCategories[0]);
    }
  }, [mainCategories]);

  const subcategories = useMemo(() => {
    return allCategories?.filter((category) => category.parent === activeCategory?.id) ?? [];
  }, [allCategories, activeCategory]);

  const handleOnHoverCategory = (category: Category) => {
    setActiveCategory(category);
  };

  const handleTouchStartCategory = (category: Category) => {
    setActiveCategory(category);
  };

  const openSubcategories = useCallback(
    (id: Category["id"], category: Category) => {
      if (allCategories?.filter((x: Category) => x.parent === id).length) {
        history.push("/categories/" + id + "/subcategories", { parentCategory: category });
      } else {
        history.push("/products/" + id + "/" + category.name + "/popular");
      }
    },
    [allCategories, history],
  );

  return (
    <Popover>
      {({ open }) => (
        <>
          <Popover.Button className="flex justify-center items-center px-2 rounded-s-md outline-none bg-[#EABC2E]">
            <div className="flex gap-1.5 text-white font-bold p-2">
              {!open ? <IonIcon src={menuOutline} className="w-6" /> : <IonIcon src={closeOutline} className="w-6" />}
              <div className="hidden lg:block">{t("Catalog")}</div>
            </div>
          </Popover.Button>

          <div className={cx("modal", open && "modalActive")}>
            <Popover.Panel className="absolute z-[11] w-auto max-h-[80vh] bg-white shadow-md rounded-2xl">
              <div className="flex w-screen max-w-[1000px] h-[80dvh] bg-white rounded-2xl overflow-hidden">
                <div className="min-w-fit flex flex-col overflow-y-auto no-scrollbar rounded-r-2xl">
                  {mainCategories?.map((mainCategory) => {
                    return (
                      <div
                        key={mainCategory.id}
                        onTouchEnd={(e) => e.preventDefault()}
                        onMouseEnter={() => handleOnHoverCategory(mainCategory)}
                        onTouchStart={() => handleTouchStartCategory(mainCategory)}
                        onClick={() => openSubcategories(mainCategory.id, mainCategory)}
                        className={cx(
                          "h-[100px] flex gap-4 items-center p-3",
                          activeCategory?.id === mainCategory.id &&
                            "rounded-r-2xl cursor-pointer text-green-500 bg-slate-100",
                        )}
                      >
                        <div className={cx("w-[60px] overflow-hidden -ml-3 rounded-r-full sm:m-0 sm:rounded-xl")}>
                          <Image
                            id="image"
                            alt="category"
                            downsize={false}
                            className="w-full scale-[1.4]"
                            src={mainCategory.image?.src}
                          />
                        </div>
                        <div className="hidden sm:block font-bold text-base">{mainCategory.name}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-col gap-4 h-[80dvh] p-5 text-lg overflow-y-auto no-scrollbar">
                  <div className="text-[28px] font-bold">{activeCategory?.name}</div>

                  <div
                    onClick={() => {
                      activeCategory && openSubcategories(activeCategory?.id, activeCategory);
                    }}
                    className="text-lg cursor-pointer md:hidden font-bold"
                  >
                    {t("All products in this category")}
                  </div>

                  {subcategories?.length > 0 ? (
                    <>
                      {subcategories?.map((subcategory) => (
                        <div
                          key={subcategory.id}
                          className="cursor-pointer hover:font-bold"
                          onClick={() => {
                            openSubcategories(subcategory.id, subcategory);
                          }}
                        >
                          {subcategory.name}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div
                      className="hover:font-bold cursor-pointer"
                      onClick={() => activeCategory && openSubcategories(activeCategory?.id!, activeCategory!)}
                    >
                      {t("View products in this category")}
                    </div>
                  )}
                </div>
              </div>
            </Popover.Panel>
          </div>
        </>
      )}
    </Popover>
  );
};
